// Shared AI assistant drawer — visual shell + state plumbing.
//
// The AI streaming backend and action-confirmation handlers are
// intentionally NOT in this file. Each consuming app registers:
//   - `aiStream(messages, attachment, onChunk, onDone, el)` function —
//     sends the prompt to the app's own AI endpoint and calls
//     `onChunk(partialText)` + `onDone(finalText)`.
//   - `aiPerformAction(action, el, s)` function — executes a proposed
//     action (create_ticket, create_event, etc.) when the user confirms.
//   - `aiActionStyles()` function — returns the `{ type → { icon, color, label } }`
//     map used to render proposed-action cards.
//
// Any app that doesn't register `aiStream` will have the toggle hidden
// automatically (AppAssistantToggle.hide reads context.functions.aiStream).
// If the toggle is shown via a consumer override, _triggerSend will
// surface a console.error and early-return instead of silently failing.

const _ATTACH = {
  ticket: { label: 'Ticket', icon: 'check' },
  document: { label: 'Document', icon: 'document' },
  event: { label: 'Event', icon: 'date outline' },
  message: { label: 'Message', icon: 'chat' },
  link: { label: 'Link', icon: 'link' }
}
const _attachMeta = (t) => _ATTACH[t] || { label: 'Item', icon: 'document' }

// Cached lookup into the consumer's `aiActionStyles` map. The function
// returns a fresh object per call so we memoize by function identity to
// avoid re-allocating on every reactive prop re-run.
let _stylesFn = null
let _stylesMap = null
const _actionStyle = (el, type) => {
  if (!type) return null
  const fn = el?.context?.functions?.aiActionStyles
  if (!fn) return null
  if (fn !== _stylesFn) {
    _stylesFn = fn
    _stylesMap = fn() || {}
  }
  return _stylesMap[type] || null
}

const _formatHtml = (text) => {
  if (!text) return ''
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code class="assistant-inline-code">$1</code>')
    .replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a class="assistant-link" href="$2" target="_blank" rel="noopener">$1</a>'
    )
    .replace(/\n/g, '<br>')
}

// Patch the last message in assistantMessages at a pinned index.
// Using an index avoids the stale-read bug of `messages[messages.length-1]`
// when concurrent updates land between SSE chunks.
const _patchMessageAt = (root, index, patch) => {
  const current = root.assistantMessages || []
  if (index < 0 || index >= current.length) return
  const updated = current.slice()
  updated[index] = { ...updated[index], ...patch }
  root.update({ assistantMessages: updated })
}

const _triggerSend = (el, s, query) => {
  if (!query || s.root.assistantTyping) return
  // Guard: aiStream must be registered. This should never fire if the toggle's
  // `hide` gate works correctly, but surfaces the misconfiguration gracefully.
  if (typeof el?.context?.functions?.aiStream !== 'function') {
    console.error('[AppAssistant] aiStream not registered — cannot send message. Register aiStream in context.functions.')
    return
  }
  console.log('[AppAssistant] streaming…', { query })
  const attachment = s.root.assistantAttachment || null
  const userMsg = {
    role: 'user',
    content: query,
    attachment: attachment ? { ...attachment } : null
  }
  const msgs = [...(s.root.assistantMessages || []), userMsg]
  const aiMsg = { role: 'assistant', content: '', _streaming: true }
  const aiIndex = msgs.length
  s.root.update({
    assistantMessages: [...msgs, aiMsg],
    assistantTyping: true,
    assistantAttachment: null
  })

  const stream = el.call(
    'aiStream',
    msgs,
    attachment,
    (text) => _patchMessageAt(s.root, aiIndex, { content: text }),
    (finalText) => {
      const actionMatch = (finalText || '').match(/<!--ACTION:(.*?)-->/)
      let actions = null
      let followUps = null
      let displayText = finalText || ''
      if (actionMatch) {
        try {
          actions = JSON.parse(actionMatch[1])
          followUps = actions.followUps || null
        } catch {}
        displayText = displayText.replace(/<!--ACTION:.*?-->/g, '').trim()
      }
      _patchMessageAt(s.root, aiIndex, {
        role: 'assistant',
        content: displayText,
        _streaming: false,
        _actions: actions,
        _followUps: followUps
      })
      s.root.update({ assistantTyping: false })
    }
  )

  if (stream && typeof stream.then === 'function') {
    stream.catch((err) => {
      console.error('[AppAssistant] aiStream failed', err)
      _patchMessageAt(s.root, aiIndex, {
        role: 'assistant',
        content: 'Connection error — please try again.',
        _streaming: false
      })
      s.root.update({ assistantTyping: false })
    })
  }
}

export const AppAssistantToggle = {
  tag: 'button',
  type: 'button',
  title: 'AI Assistant',
  'aria-label': 'Toggle AI Assistant',
  padding: 'Z',
  round: 'Z',
  cursor: 'pointer',
  background: 'transparent',
  color: 'caption',
  ':hover': { background: 'overlay', color: 'title' },
  // Gate on aiStream registration so apps without a backend don't show
  // the toggle. Context functions are set at boot — this is a one-time
  // check, not reactive. Consumers that override this hide must also gate
  // on hasAiStream (or this condition) to avoid showing the button on
  // surfaces with no AI backend.
  hide: (el) => {
    const fn = el?.context?.functions?.aiStream
    const missing = typeof fn !== 'function'
    if (missing && process.env.NODE_ENV !== 'production') {
      // Warn only once per element (not on every re-render). If you see
      // this warning, register an `aiStream` function in the project's
      // functions before showing the AI assistant toggle.
      if (!el._aiStreamWarnFired) {
        el._aiStreamWarnFired = true
        console.warn('[AppAssistantToggle] aiStream not registered — toggle hidden.')
      }
    }
    return missing
  },
  onClick: (e, el, s) => {
    s.root.update({ assistantOpen: !s.root.assistantOpen })
  },
  Icon: { icon: 'magicstar', boxSize: 'A A' }
}

export const AppAssistant = {
  extends: 'CommonCard',
  // Warn once on mount when aiStream is not registered but the panel is
  // being shown anyway. This surfaces the inheritance-from-shared-lib
  // regression early (toggle override that skips the aiStream gate).
  // Also start the realtime ticket subscription so Simona auto-surfaces
  // when user-originated tickets ship. The call is guarded — apps that
  // don't register `subscribeTicketRealtime` (e.g. canvas) are skipped
  // silently; no subscription is opened.
  onInit: (el, s) => {
    if (typeof el?.context?.functions?.aiStream !== 'function') {
      console.warn(
        '[AppAssistant] mounted without aiStream registered. ' +
        'The panel will render but Send will do nothing. ' +
        'Register aiStream in context.functions or hide the toggle.'
      )
    }
    if (typeof el?.context?.functions?.subscribeTicketRealtime === 'function') {
      el.call('subscribeTicketRealtime', s.root)
    }
  },
  // Tear down the realtime channel when the panel is removed from the DOM
  // to avoid leaking open WebSocket channels after navigation.
  onRemove: (el) => {
    if (typeof el?.context?.functions?.unsubscribeTicketRealtime === 'function') {
      el.call('unsubscribeTicketRealtime')
    }
  },
  hide: (el, s) => !s.root.assistantOpen,
  flow: 'x',
  width: (el, s) => {
    const w = Number(s.root.assistantWidth)
    return Number.isFinite(w) && w > 0 ? w + 'px' : 'H'
  },
  minWidth: 'G+A',
  maxWidth: 'J',
  padding: '0',
  color: 'title',
  overflow: 'hidden',
  position: 'fixed',
  top: '0',
  right: '0',
  height: '100dvh',
  zIndex: '10000',
  round: '0',
  borderLeftWidth: '1px',
  borderLeftColor: 'line',
  borderLeftStyle: 'solid',
  // Global cursor + select lock during resize — done via reactive prop on
  // the drawer root instead of mutating `document.body.style` so it stays
  // within DOMQL's declarative model.
  cursor: (el, s) => (s.root.assistantResizing ? 'col-resize' : ''),
  userSelect: (el, s) => (s.root.assistantResizing ? 'none' : ''),

  ResizeHandle: {
    width: 'V',
    cursor: 'col-resize',
    background: 'transparent',
    borderLeftWidth: '1px',
    borderLeftColor: 'line',
    borderLeftStyle: 'solid',
    flexShrink: '0',
    ':hover': { background: 'blue.15' },

    onMousedown: (e, el, s) => {
      e.preventDefault()
      const rect = el.parent?.node?.getBoundingClientRect?.()
      el._dragStartX = e.clientX
      el._dragStartW = rect ? rect.width : 500
      s.root.update({ assistantResizing: true })
      if (el.node?.setPointerCapture && e.pointerId != null) {
        try { el.node.setPointerCapture(e.pointerId) } catch {}
      }
    },

    onMousemove: (e, el, s) => {
      if (!s.root.assistantResizing) return
      if (el._dragStartX == null) return
      const dx = el._dragStartX - e.clientX
      const next = Math.max(250, Math.min(800, el._dragStartW + dx))
      if (next === s.root.assistantWidth) return
      s.root.update({ assistantWidth: next })
    },

    onMouseup: (e, el, s) => {
      if (!s.root.assistantResizing) return
      el._dragStartX = null
      el._dragStartW = null
      s.root.update({ assistantResizing: false })
      if (el.node?.releasePointerCapture && e.pointerId != null) {
        try { el.node.releasePointerCapture(e.pointerId) } catch {}
      }
    },

    onMouseleave: (e, el, s) => {
      if (!s.root.assistantResizing) return
      el._dragStartX = null
      el._dragStartW = null
      s.root.update({ assistantResizing: false })
    }
  },

  Panel: {
    flow: 'y',
    flex: '1',
    minWidth: '0',
    overflow: 'hidden',

    Header: {
      flow: 'x',
      align: 'center space-between',
      padding: 'Z1 A',
      borderBottomWidth: '1px',
      borderBottomColor: 'line',
      borderBottomStyle: 'solid',
      minHeight: 'D',

      TitleGroup: {
        flow: 'x',
        align: 'center left',
        gap: 'Z',
        AiIcon: {
          extends: 'Icon',
          icon: 'star',
          boxSize: 'A1 A1',
          color: 'caption'
        },
        Title: {
          tag: 'h3',
          text: 'AI Assistant',
          margin: '0',
          fontSize: 'A',
          fontWeight: '600'
        }
      },

      CloseBtn: {
        tag: 'button',
        type: 'button',
        padding: 'Z',
        round: 'Z',
        cursor: 'pointer',
        background: 'transparent',
        borderWidth: '0',
        color: 'caption',
        ':hover': { background: 'overlay', color: 'title' },
        onClick: (e, el, s) => {
          s.root.update({ assistantOpen: false })
        },
        Icon: { icon: 'x', boxSize: 'Z2 Z2' }
      }
    },

    Thread: {
      flex: '1',
      flow: 'y',
      gap: 'A',
      padding: 'A',
      overflow: 'auto',
      minHeight: '0',

      // Auto-scroll to bottom when a new message arrives. Reads scrollHeight
      // (Rule 39 allows reading `el.node`) and calls `.scrollTo()` — a
      // native method with no DOMQL equivalent, treated like `.focus()`.
      // Also retries the realtime ticket subscription if user identity was
      // not yet available at onInit time (auth resolves asynchronously after
      // the panel mounts).
      onUpdate: (el, s) => {
        if (!el?.node) return
        const msgs = s.root.assistantMessages || []
        const sig = msgs.length
        if (sig !== el._lastLen) {
          el._lastLen = sig
          const win = el.context?.window || window
          win.requestAnimationFrame(() => {
            if (el.node && typeof el.node.scrollTo === 'function') {
              el.node.scrollTo({ top: el.node.scrollHeight })
            }
          })
        }
        // Retry subscription once user identity becomes available.
        // subscribeTicketRealtime is idempotent: no-ops when already subscribed
        // for the same identity.
        const userId = s.root?.currentUserId || s.root?.currentUserEmail
        if (userId && !el._realtimeSubscribed) {
          if (typeof el?.context?.functions?.subscribeTicketRealtime === 'function') {
            el.call('subscribeTicketRealtime', s.root)
            el._realtimeSubscribed = true
          }
        }
      },

      Welcome: {
        if: (el, s) =>
          !(s.root.assistantMessages || []).length && !s.root.assistantTyping,
        flow: 'y',
        align: 'center center',
        gap: 'A',
        flex: '1',
        padding: 'C A',
        textAlign: 'center',

        WelcomeIcon: {
          extends: 'Icon',
          icon: 'star',
          boxSize: 'D D',
          color: 'caption',
          opacity: '.4'
        },
        WelcomeText: {
          tag: 'p',
          margin: '0',
          fontSize: 'Z2',
          color: 'caption',
          lineHeight: '1.6',
          maxWidth: 'G',
          text: (el) => el.call('aiWelcomeText') || 'Ask me anything.'
        },

        Suggestions: {
          flow: 'x wrap',
          gap: 'Z',
          justifyContent: 'center',
          marginTop: 'Z',
          childExtends: 'AppAssistantSuggestion',
          childrenAs: 'state',
          children: (el) => el.call('aiSuggestions') || []
        }
      },

      Messages: {
        if: (el, s) =>
          (s.root.assistantMessages || []).length > 0 || s.root.assistantTyping,
        flow: 'y',
        gap: 'A',
        childExtends: 'AppAssistantMessage',
        childrenAs: 'state',
        children: (el, s) => s.root.assistantMessages || []
      }
    },

    TypingIndicator: {
      if: (el, s) => s.root.assistantTyping,
      flow: 'x',
      gap: 'Y',
      align: 'center left',
      padding: 'Z A',
      Label: {
        tag: 'span',
        text: 'Thinking',
        fontSize: 'Y2',
        color: 'caption',
        fontWeight: '500'
      },
      Dot_1: {
        width: 'Y',
        height: 'Y',
        round: '100%',
        background: 'caption',
        animation: 'pulse 1.2s ease-in-out infinite 0s'
      },
      Dot_2: {
        width: 'Y',
        height: 'Y',
        round: '100%',
        background: 'caption',
        animation: 'pulse 1.2s ease-in-out infinite 0.2s'
      },
      Dot_3: {
        width: 'Y',
        height: 'Y',
        round: '100%',
        background: 'caption',
        animation: 'pulse 1.2s ease-in-out infinite 0.4s'
      }
    },

    InputArea: {
      flow: 'y',
      gap: 'Z',
      padding: 'Z A A A',
      borderTopWidth: '1px',
      borderTopColor: 'line',
      borderTopStyle: 'solid',

      AttachmentPreview: {
        // Use `if` (DOM-level removal) rather than `hide` (CSS display:none)
        // so the chip is entirely absent from the DOM — and its child text
        // factories never run — when no attachment is set. `hide` only applies
        // CSS and can still render child text (e.g. "Item") if the reactive
        // display update lags behind the initial DOM paint.
        if: (el, s) => !!s.root.assistantAttachment,
        flow: 'x',
        gap: 'Z',
        align: 'center',
        padding: 'Z Z1',
        round: 'Z',
        borderWidth: '1px',
        borderColor: 'blue.3',
        borderStyle: 'solid',
        background: 'blue.05',

        AttachIcon: {
          extends: 'Icon',
          icon: (el, s) => _attachMeta(s.root.assistantAttachment?.type).icon,
          boxSize: 'A A',
          color: 'blue',
          flexShrink: '0'
        },
        AttachInfo: {
          flow: 'y',
          gap: 'V',
          flex: '1',
          minWidth: '0',
          TypeLabel: {
            tag: 'span',
            fontSize: 'Y2',
            fontWeight: '600',
            color: 'blue',
            text: (el, s) => _attachMeta(s.root.assistantAttachment?.type).label
          },
          AttachTitle: {
            tag: 'span',
            fontSize: 'Z2',
            color: 'title',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            text: (el, s) => s.root.assistantAttachment?.title || ''
          }
        },
        RemoveBtn: {
          tag: 'button',
          type: 'button',
          padding: 'V',
          round: 'V',
          background: 'transparent',
          borderWidth: '0',
          color: 'caption',
          cursor: 'pointer',
          fontSize: 'Z2',
          text: '✕',
          ':hover': { color: 'title' },
          onClick: (e, el, s) => {
            s.root.update({ assistantAttachment: null })
          }
        }
      },

      ComposerBox: {
        placeholder: 'Ask a question…',
        maxWidth: '100%',
        // _onSend (underscore prefix) avoids DOMQL treating this as the
        // DOM 'send' event handler — see ComposerBox.js for full rationale.
        _onSend: (body, el, s) => {
          _triggerSend(el, s, body || 'Tell me about this')
        }
      }
    }
  }
}

export const AppAssistantSuggestion = {
  tag: 'button',
  type: 'button',
  padding: 'Z A',
  round: 'C',
  fontSize: 'Z2',
  fontWeight: '500',
  cursor: 'pointer',
  fontFamily: 'inherit',
  borderWidth: '1px',
  borderColor: 'line',
  borderStyle: 'solid',
  background: 'transparent',
  color: 'caption',
  transition: 'all 120ms ease',
  ':hover': { borderColor: 'title', color: 'title', background: 'overlay' },
  text: (el, s) => s?.text || '',
  onClick: (e, el, s) => {
    const query = s?.text || ''
    if (query) _triggerSend(el, s, query)
  }
}

export const AppAssistantMessage = {
  flow: 'y',
  gap: 'Z',

  AttachedCard: {
    hide: (el, s) => !s.attachment,
    flow: 'x',
    gap: 'Z',
    align: 'center',
    padding: 'Z Z1',
    round: 'Z',
    borderWidth: '1px',
    borderColor: 'blue.3',
    borderStyle: 'solid',
    background: 'blue.05',
    alignSelf: 'flex-end',
    maxWidth: '90%',

    CardIcon: {
      extends: 'Icon',
      icon: (el, s) => _attachMeta(s.attachment?.type).icon,
      boxSize: 'Z2 Z2',
      color: 'blue'
    },
    CardLabel: {
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: '600',
      color: 'blue',
      text: (el, s) => _attachMeta(s.attachment?.type).label
    },
    CardTitle: {
      tag: 'span',
      fontSize: 'Z2',
      color: 'title',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => s.attachment?.title || ''
    }
  },

  Bubble: {
    padding: 'Z1 A',
    round: 'Z',
    background: 'transparent',
    alignSelf: 'flex-start',
    maxWidth: '90%',
    borderWidth: '1px',
    borderColor: 'line',
    borderStyle: 'solid',
    isUser: (el, s) => s.role === 'user',
    '.isUser': {
      background: 'blue.1',
      alignSelf: 'flex-end',
      borderWidth: '0',
      borderStyle: 'none'
    },

    RoleLabel: {
      tag: 'span',
      fontSize: 'Y1',
      fontWeight: '600',
      letterSpacing: '.04em',
      color: 'caption',
      textTransform: 'uppercase',
      marginBottom: 'V',
      text: (el, s) => (s.role === 'user' ? 'You' : 'AI')
    },

    // AI output is lightweight markdown (bold/italic/code/link/linebreak).
    // Rendered via `html:` because expanding the markdown into DOMQL children
    // would require a full parser — out of scope for this shell. Content is
    // produced by our AI server, not user input, so the XSS surface is
    // limited to the model's own output.
    MsgContent: {
      tag: 'div',
      html: (el, s) => _formatHtml(s.content),
      fontSize: 'Z2',
      lineHeight: '1.65',
      color: 'title'
    }
  },

  ActionCard: {
    hide: (el, s) =>
      !(
        s._actions &&
        s._actions.type &&
        s._actions.type !== 'info' &&
        !s._actions._confirmed
      ),
    flow: 'y',
    gap: 'Z',
    padding: 'A',
    round: 'Z',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: (el, s) => {
      const style = _actionStyle(el, s._actions?.type)
      return style ? `${style.color}.3` : 'line'
    },
    background: (el, s) => {
      const style = _actionStyle(el, s._actions?.type)
      return style ? `${style.color}.06` : 'overlay'
    },
    alignSelf: 'flex-start',
    maxWidth: '90%',

    CardHeader: {
      flow: 'x',
      gap: 'Z',
      align: 'center left',
      Icon: {
        boxSize: 'A A',
        icon: (el, s) => _actionStyle(el, s._actions?.type)?.icon || 'state',
        color: (el, s) => _actionStyle(el, s._actions?.type)?.color || 'caption'
      },
      Label: {
        tag: 'span',
        fontSize: 'Y2',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '.04em',
        color: (el, s) => _actionStyle(el, s._actions?.type)?.color || 'caption',
        text: (el, s) => _actionStyle(el, s._actions?.type)?.label || 'Action'
      }
    },

    CardTitle: {
      tag: 'h4',
      margin: '0',
      fontSize: 'Z2',
      fontWeight: '600',
      color: 'title',
      text: (el, s) => {
        const a = s._actions
        if (!a) return ''
        if (a.title) return a.title
        if (a.number) return `#${a.number}`
        return ''
      }
    },

    CardMeta: {
      hide: (el, s) => {
        const a = s._actions
        return !(
          a?.body ||
          a?.date ||
          a?.priority ||
          a?.category ||
          a?.column_key
        )
      },
      tag: 'p',
      margin: '0',
      fontSize: 'Y2',
      color: 'caption',
      lineHeight: '1.4',
      text: (el, s) => {
        const a = s._actions
        if (!a) return ''
        const parts = []
        if (a.body)
          parts.push(a.body.slice(0, 80) + (a.body.length > 80 ? '...' : ''))
        if (a.date) parts.push(`Date: ${a.date}`)
        if (a.startTime) parts.push(`${a.startTime}–${a.endTime || ''}`)
        if (a.priority) parts.push(`Priority: ${a.priority}`)
        if (a.category) parts.push(`Category: ${a.category}`)
        if (a.assignee) parts.push(`→ ${a.assignee}`)
        return parts.join(' · ')
      }
    },

    CardActions: {
      flow: 'x',
      gap: 'Z',
      marginTop: 'V',

      ConfirmBtn: {
        extends: 'Button',
        theme: 'primary',
        padding: 'Z A',
        fontSize: 'Z2',
        text: 'Confirm',
        onClick: (e, el, s) => {
          const a = s._actions
          if (!a) return
          try {
            el.call('aiPerformAction', a, s)
          } catch (err) {
            console.error('[AppAssistant] aiPerformAction failed', err)
          }
          const root = s.root
          const msgs = root.assistantMessages || []
          const idx = msgs.indexOf(s)
          if (idx >= 0) {
            const updated = msgs.slice()
            updated[idx] = {
              ...updated[idx],
              _actions: { ...a, _confirmed: true }
            }
            root.update({ assistantMessages: updated })
          }
        }
      },

      CancelBtn: {
        tag: 'button',
        type: 'button',
        padding: 'Z A',
        fontSize: 'Z2',
        fontWeight: '500',
        background: 'transparent',
        borderWidth: '0',
        color: 'caption',
        cursor: 'pointer',
        fontFamily: 'inherit',
        text: 'Cancel',
        ':hover': { color: 'title' },
        onClick: (e, el, s) => {
          const root = s.root
          const msgs = root.assistantMessages || []
          const idx = msgs.indexOf(s)
          if (idx >= 0) {
            const updated = msgs.slice()
            updated[idx] = { ...updated[idx], _actions: null }
            root.update({ assistantMessages: updated })
          }
        }
      }
    }
  },

  ConfirmedCard: {
    hide: (el, s) => !s._actions?._confirmed,
    flow: 'x',
    gap: 'Z',
    align: 'center left',
    padding: 'Z A',
    round: 'Z',
    background: 'green.08',
    borderWidth: '1px',
    borderColor: 'green.3',
    borderStyle: 'solid',
    alignSelf: 'flex-start',
    maxWidth: '90%',

    Icon: { icon: 'check', boxSize: 'A A', color: 'green' },
    Label: {
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: '600',
      color: 'green',
      text: (el, s) => {
        const a = s._actions
        if (!a) return 'Done'
        const style = _actionStyle(el, a.type)
        return `✓ ${style?.label || 'Action'} — ${a.title || '#' + (a.number || '')}`
      }
    }
  },

  FollowUps: {
    hide: (el, s) => {
      if (s.role !== 'assistant' || s._streaming) return true
      const fups = s._followUps || s.followUps
      return !(fups && fups.length)
    },
    flow: 'x wrap',
    gap: 'Y',
    marginTop: 'V',
    childExtends: 'AppAssistantSuggestion',
    childrenAs: 'state',
    children: (el, s) => {
      const fups = s._followUps || s.followUps || []
      return fups.map((q, i) => ({ key: i, text: q }))
    }
  }
}
